import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, X, RefreshCw, HelpCircle, AlertCircle, Printer, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ['New', 'Urgent', 'Attempting Contact', 'Contacted', 'Qualified', 'Application Started', 'Awaiting Documents', 'Under Review', 'Bond Posted', 'Closed Won', 'Closed Lost', 'Invalid / Duplicate', 'Do Not Contact'];

const Hint = ({ children }) => (
  <div className="amg-hint">
    <HelpCircle size={15} />
    <span>{children}</span>
  </div>
);

const FILTERS = [
  { key: 'all', label: 'ALL', match: () => true },
  { key: 'new', label: 'NEW', match: (l) => /new|urgent/i.test(l.status || '') && !/attempt|contact|qualified|bond|closed/i.test(l.status || '') },
  { key: 'contacted', label: 'CONTACTED', match: (l) => /contact/i.test(l.status || '') },
  { key: 'posted', label: 'POSTED', match: (l) => /bond posted|closed won/i.test(l.status || '') },
];

const statusPill = (status) => {
  const s = (status || 'New').toString();
  if (/urgent/i.test(s)) return <span className="badge-new">URGENT</span>;
  if (/new/i.test(s) && !/attempt|contact|qualified/i.test(s)) return <span className="badge-new">NEW</span>;
  if (/bond posted|closed won/i.test(s)) return <span className="badge-printed">POSTED</span>;
  return <span className="badge-viewed">{s.toUpperCase()}</span>;
};

const LeadInbox = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [language, setLanguage] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/leads`, { params: { search, status_filter: status, language, page, limit } })
      .then((res) => {
        setLeads(res.data.leads);
        setTotal(res.data.total);
      })
      .finally(() => setLoading(false));
  }, [search, status, language, page]);

  useEffect(() => {
    load();
  }, [load]);

  const displayed = leads.filter((l) => FILTERS.find((f) => f.key === filter).match(l));
  const newCount = leads.filter((l) => FILTERS[1].match(l)).length;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div data-testid="dash-lead-inbox">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="dash-content__title" data-testid="submissions-title">All Submissions</h1>
        <p className="dash-content__subtitle">
          {total} total {newCount > 0 && <span style={{ color: 'var(--red-bright)', fontWeight: 700 }}>({newCount} NEW)</span>}
        </p>
      </div>

      <Hint>
        This is where every bond application your clients submit lives. Use the search bar to find anyone by name, phone, or reference ID. Click on any row to open the full details and print the PDFs.
      </Hint>

      {/* Filter buttons + search */}
      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem', fontFamily: 'var(--font-body)' }}>
        Filter: <strong>ALL</strong> = everything, <strong>NEW</strong> = not yet touched, <strong>CONTACTED</strong> = family has been reached, <strong>POSTED</strong> = bond posted.
      </div>

      <div style={{ display: 'flex', gap: '0.9rem', marginBottom: '1.4rem', flexWrap: 'wrap', alignItems: 'center' }} data-testid="search-filter-bar">
        <div style={{ flex: 1, minWidth: 280, position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            className="form-input"
            placeholder="Search name, phone, reference ID\u2026"
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            style={{ paddingLeft: '2.7rem', fontSize: '1rem' }}
            data-testid="lead-search-input"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={filter === f.key ? 'amg-btn-primary' : 'amg-btn-outline'}
              style={{ padding: '0.6rem 1.1rem', fontSize: '0.85rem' }}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={load} className="amg-btn-outline" style={{ padding: '0.6rem 0.8rem' }} data-testid="refresh-btn" aria-label="Refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Advanced filters row (status + language) */}
      <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', marginBottom: '1.4rem' }}>
        <select className="form-input" style={{ maxWidth: 260 }} value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} data-testid="lead-status-filter">
          <option value="">All Statuses</option>
          {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
        <select className="form-input" style={{ maxWidth: 200 }} value={language} onChange={(e) => { setPage(1); setLanguage(e.target.value); }} data-testid="lead-language-filter">
          <option value="">All Languages</option>
          <option value="en">English</option>
          <option value="es">Español</option>
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>Loading\u2026</div>
      ) : displayed.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', background: 'var(--surface)', border: '1px solid var(--border-dim)', color: 'var(--text-muted)' }}>
          {search ? 'No submissions match your search.' : 'No submissions yet.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {displayed.map((sub, i) => {
            const isNew = FILTERS[1].match(sub);
            return (
              <div
                key={sub.id || i}
                data-testid={`submission-row-${sub.id}`}
                onClick={() => navigate(`/dashboard/leads/${sub.id}`)}
                style={{
                  background: isNew ? 'rgba(122, 30, 40, 0.06)' : 'var(--surface)',
                  border: isNew ? '2px solid rgba(122, 30, 40, 0.3)' : '1px solid var(--border-dim)',
                  cursor: 'pointer',
                  padding: '1.15rem 1.4rem',
                  display: 'flex',
                  gap: '1.4rem',
                  alignItems: 'center',
                  transition: 'border-color 0.25s ease, background 0.25s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = isNew ? 'rgba(122, 30, 40, 0.3)' : 'var(--border-dim)'; }}
              >
                <div style={{ minWidth: 100 }}>{statusPill(sub.status)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--text-primary)', marginBottom: '0.15rem' }}>
                    {sub.defendant_name || sub.requester_name || 'Unknown'}
                    {sub.is_possible_duplicate && <AlertCircle size={13} color="var(--gold)" style={{ marginLeft: 6, verticalAlign: 'middle' }} title="Possible duplicate" />}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    Ref: {sub.id} | {sub.jail_city || '\u2014'} | {sub.created_at ? new Date(sub.created_at).toLocaleString() : 'N/A'}
                    {sub.assigned_to ? ` | ${sub.assigned_to}` : ' | Unassigned'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Eye size={18} style={{ color: 'var(--gold)' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem', alignItems: 'center', color: 'var(--text-secondary)', fontSize: '0.86rem' }}>
        <button className="amg-btn-outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="lead-page-prev">Prev</button>
        <span>Page {page} of {totalPages}</span>
        <button className="amg-btn-outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="lead-page-next">Next</button>
      </div>
    </div>
  );
};

export default LeadInbox;
